import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCw, Check, Users, User, Star, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { track } from '@/utils/analytics';
import { httpFailureReason } from '@/utils/analytics-events';
import { withPhotoConsent } from '@/components/consent/photo-consent-gate';

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DetectedFace {
  id: string;
  number: number;
  bbox: BoundingBox;
  expanded_bbox: BoundingBox;
  confidence: number;
  thumbnail: string;
  selected?: boolean;
  name?: string;
}

interface FaceDetectionWizardProps {
  imageUrl: string;
  onFacesConfirmed: (faces: DetectedFace[], mainCharacterIndex: number) => void;
  onManualMode: () => void;
  form?: any; // Add form prop to update detectedFaces in real-time
  /** The parent's consent tick, sent as photoConsent with the photo (R3). Absent for the free-cover photo (R5). */
  photoConsent?: string | null;
  /** The free cover's own photo with no tick: the server inherits the cover's stored consent (R5, owner proof). */
  fromCoverSlug?: string | null;
}

export interface FaceDetectionWizardRef {
  validateAndGetData: () => { faces: DetectedFace[], mainIndex: number } | null;
}

export const FaceDetectionWizard = forwardRef<FaceDetectionWizardRef, FaceDetectionWizardProps>(({ 
  imageUrl, 
  onFacesConfirmed,
  onManualMode,
  form,
  photoConsent,
  fromCoverSlug
}, ref) => {
  const [isDetecting, setIsDetecting] = useState(true);
  const [detectedFaces, setDetectedFaces] = useState<DetectedFace[]>([]);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [displayDimensions, setDisplayDimensions] = useState({ width: 0, height: 0 });
  const [mainCharacterIndex, setMainCharacterIndex] = useState<number>(0);
  const imageRef = useRef<HTMLImageElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Expose validation method to parent
  useImperativeHandle(ref, () => ({
    validateAndGetData: () => {
      const selectedFaces = detectedFaces.filter(f => f.selected);
      const allHaveNames = selectedFaces.every(face => face.name && face.name.trim() !== '');
      
      if (selectedFaces.length === 0) {
        toast({
          title: "No faces selected",
          description: "Please select at least one person",
          variant: "destructive"
        });
        return null;
      }
      
      if (!allHaveNames) {
        toast({
          title: "Names required",
          description: "Please enter a name for each selected character",
          variant: "destructive"
        });
        return null;
      }
      
      return {
        faces: selectedFaces,
        mainIndex: Math.min(mainCharacterIndex, selectedFaces.length - 1)
      };
    }
  }));

  useEffect(() => {
    detectFaces();
  }, [imageUrl]);

  // Remove the automatic AI analysis trigger - we'll do it differently
  // useEffect(() => {
  //   if (!isDetecting && detectedFaces.length > 0) {
  //     const selectedFaces = detectedFaces.filter(f => f.selected);
  //     const allHaveNames = selectedFaces.every(face => face.name && face.name.trim() !== '');
      
  //     if (allHaveNames && selectedFaces.length > 0) {
  //       // Trigger the AI analysis
  //       console.log('[FaceDetectionWizard] All faces have names, triggering AI analysis');
  //       const triggerAIAnalysis = (window as any).__triggerAIAnalysis;
  //       if (triggerAIAnalysis) {
  //         triggerAIAnalysis(selectedFaces);
  //       }
  //     }
  //   }
  // }, [detectedFaces, mainCharacterIndex, isDetecting]);

  // Update form when faces are selected/updated
  useEffect(() => {
    if (form && !isDetecting) {
      console.log('[FaceDetectionWizard] Updating form with faces:', detectedFaces);
      form.setValue('detectedFaces', detectedFaces);
      
      // Trigger form validation after updating
      setTimeout(() => {
        form.trigger(['detectedFaces']);
      }, 100);
    }
  }, [detectedFaces, form, isDetecting]);

  const detectFaces = async () => {
    console.log('[WIZARD] Starting face detection...');
    setIsDetecting(true);
    
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      
      const formData = withPhotoConsent(new FormData(), photoConsent);
      if (!photoConsent && fromCoverSlug) formData.set('fromCoverSlug', fromCoverSlug);
      formData.append('file', file);

      const result = await fetch('/api/detect-faces', {
        method: 'POST',
        body: formData
      });
      
      const data = await result.json();
      
      if (data.success && data.faces.length > 0) {
        // Auto-select all faces up to 3 and initialize empty names
        const facesWithSelection = data.faces.map((face: DetectedFace, index: number) => ({ 
          ...face, 
          selected: index < 5, // Auto-select first 5 faces
          name: '' // Initialize with empty name
        }));
        setDetectedFaces(facesWithSelection);
        setImageDimensions(data.image_dimensions);
        track('faces_detected', { face_count: data.faces.length });
      } else {
        track('face_detection_failed', { reason: result.ok ? 'no_faces' : httpFailureReason(result.status) });
        toast({
          title: "No faces detected",
          description: "Please use manual selection",
          variant: "destructive"
        });
        onManualMode();
      }
    } catch (error) {
      console.error('[WIZARD] Face detection error:', error);
      track('face_detection_failed', { reason: 'network' });
      toast({
        title: "Detection failed",
        description: "Switching to manual mode",
        variant: "destructive"
      });
      onManualMode();
    } finally {
      setIsDetecting(false);
    }
  };

  const toggleFaceSelection = (faceId: string) => {
    setDetectedFaces(faces => {
      const updatedFaces = faces.map(face => 
        face.id === faceId ? { ...face, selected: !face.selected } : face
      );
      
      // Check selection limits
      const selectedCount = updatedFaces.filter(f => f.selected).length;
      if (selectedCount > 3) {
        toast({
          title: "Maximum 5 people",
          description: "You can select up to 5 people for your story",
          variant: "destructive"
        });
        return faces; // Don't update if over limit
      }
      
      // Reset main character index if needed
      if (selectedCount > 0 && mainCharacterIndex >= selectedCount) {
        setMainCharacterIndex(0);
      }
      
      return updatedFaces;
    });
  };

  const updateName = (faceId: string, name: string) => {
    setDetectedFaces(faces => 
      faces.map(face => 
        face.id === faceId ? { ...face, name } : face
      )
    );
    
    // Trigger validation when name changes
    if (form) {
      setTimeout(() => {
        form.trigger(['detectedFaces']);
      }, 100);
    }
  };

  // Update mainCharacterIndex in form when it changes
  useEffect(() => {
    if (form && !isDetecting) {
      form.setValue('mainCharacterIndex', mainCharacterIndex);
    }
  }, [mainCharacterIndex, form, isDetecting]);

  const getScaledPosition = (bbox: BoundingBox) => {
    if (!imageRef.current || imageDimensions.width === 0 || displayDimensions.width === 0) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }
    
    const scaleX = displayDimensions.width / imageDimensions.width;
    const scaleY = displayDimensions.height / imageDimensions.height;
    
    return {
      left: bbox.x * scaleX,
      top: bbox.y * scaleY,
      width: bbox.width * scaleX,
      height: bbox.height * scaleY
    };
  };

  const selectedFaces = detectedFaces.filter(f => f.selected);
  const selectedCount = selectedFaces.length;
  const allHaveNames = selectedFaces.every(face => face.name && face.name.trim() !== '');

  return (
    <div className="space-y-4" data-testid="face-detection-wizard">
      {isDetecting ? (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-16 space-y-3"
        >
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-gray-600">Finding faces in your photo...</p>
        </motion.div>
      ) : (
        <>
          {/* Image with face boxes */}
          <div className="relative inline-block mx-auto w-full text-center">
            <div className="relative inline-block">
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Uploaded photo"
                className="max-w-full h-auto rounded-lg shadow-sm"
                style={{ maxHeight: '300px' }}
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                  setDisplayDimensions({ width: img.width, height: img.height });
                }}
              />
              
              {/* Face boxes */}
              {detectedFaces.map((face) => {
                const position = getScaledPosition(face.bbox);
                return (
                  <motion.div
                    key={face.id}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`absolute border-3 rounded cursor-pointer transition-all ${
                      face.selected 
                        ? 'border-purple-500' 
                        : 'border-gray-400 hover:border-gray-500'
                    }`}
                    style={{
                      left: `${position.left}px`,
                      top: `${position.top}px`,
                      width: `${position.width}px`,
                      height: `${position.height}px`,
                      backgroundColor: face.selected ? 'rgba(147, 51, 234, 0.1)' : 'rgba(156, 163, 175, 0.1)'
                    }}
                    onClick={() => toggleFaceSelection(face.id)}
                  >
                    <div 
                      className={`absolute px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        face.selected 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-gray-700 text-white'
                      }`}
                      style={{
                        left: '50%',
                        transform: 'translateX(-50%)',
                        top: '-25px'
                      }}
                    >
                      Person {face.number}
                      {face.selected && <Check className="inline w-3 h-3 ml-1" />}
                    </div>
                  </motion.div>
                );
              })}
              
              {/* Retry button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={detectFaces}
                disabled={isDetecting}
                className="absolute top-2 right-2 bg-white/80 backdrop-blur"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${isDetecting ? 'animate-spin' : ''}`} />
                Retry
              </Button>
            </div>
          </div>

          {/* Character details section */}
          {selectedCount > 0 ? (
            <Card className="p-4 border-2 border-purple-300 bg-purple-50/50">
              <h4 className="font-semibold text-lg text-purple-900 mb-1">Name Your Selected Characters</h4>
              <p className="text-sm text-purple-700 mb-3">Enter a name for each person below to continue</p>
              <div className="space-y-3">
                {selectedFaces.map((face, index) => (
                  <div key={face.id} className="flex items-center gap-3">
                    {/* Thumbnail */}
                    {face.thumbnail ? (
                      <img 
                        src={face.thumbnail} 
                        alt={`Person ${face.number}`}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Name input */}
                    <Input
                      ref={index === 0 ? firstNameInputRef : undefined}
                      placeholder={`Name for Person ${face.number}`}
                      value={face.name || ''}
                      onChange={(e) => updateName(face.id, e.target.value.trim())}
                      onBlur={(e) => updateName(face.id, e.target.value.trim())}
                      className="flex-1"
                    />
                    
                    {/* Main character radio button */}
                    {selectedCount > 1 && (
                      <RadioGroup value={String(mainCharacterIndex)} onValueChange={(value) => setMainCharacterIndex(parseInt(value))}>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value={String(index)} id={`main-${face.id}`} />
                          <Label htmlFor={`main-${face.id}`} className="text-sm cursor-pointer whitespace-nowrap">
                            Main
                          </Label>
                        </div>
                      </RadioGroup>
                    )}
                  </div>
                ))}
              </div>
              
              {selectedCount > 1 && (
                <p className="text-xs text-gray-500 mt-3">
                  The main character will be the focus of the story
                </p>
              )}
            </Card>
          ) : (
            <Card className="p-4 bg-amber-50 border-amber-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">No faces selected</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Click on the boxes in the photo to select people for your story
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Status bar */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {selectedCount > 0 ? (
                <>
                  <span className="font-medium">{selectedCount}</span> of {detectedFaces.length} selected
                </>
              ) : (
                'Click faces to select'
              )}
            </span>
            {!allHaveNames && selectedCount > 0 && (
              <span className="text-amber-600 text-xs">
                Enter names for all characters
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
});

FaceDetectionWizard.displayName = 'FaceDetectionWizard';
